import { useState } from 'react';
import { Link } from 'react-router';
import { api, type ChangeSetView, type SystemInfo } from '../../api/client';
import { ChangeSetDetail, ChangeSetFeed } from '../../components/changesets';
import { Card, Chip, Drawer, Empty, Kpi, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { plural } from '../../lib/format';
import { useData } from '../../lib/useData';

export function AdminOverview() {
  const system = useData(() => api.system(), []);
  const blockers = useData(() => api.blockers(), []);
  const tenants = useData(() => api.tenants(), []);
  const changes = useData(() => api.allChangeSets(undefined, 200), []);
  const [open, setOpen] = useState<ChangeSetView | null>(null);

  const s = system.data;
  const people = tenants.data?.tenants.reduce((n, t) => n + (t.extensionCount ?? 0), 0);
  const all = changes.data?.changeSets ?? [];
  const attention = all.filter((c) => c.status === 'needs_attention');
  const waiting = all.filter((c) => c.status === 'planned');
  const unverified = s?.endpoints.filter((e) => !e.verified).length ?? 0;

  return (
    <>
      <PageHeader eyebrow="Platform · all tenants" title="Overview" />
      <div className="content">
        {system.error && <Notice tone="crit" title="Couldn't load system status">{system.error.message}</Notice>}
        <section aria-label="Platform health" className="kpis">
          <Kpi
            label="Phone system"
            small
            value={!s ? '…' : s.pbx.reachable ? `Vodia ${s.pbx.version || '?'}` : 'Unreachable'}
            foot={!s ? ' ' : s.pbx.reachable ? (s.pbx.supported ? 'Reachable · supported version' : 'Reachable · version not confirmed') : `Error: ${s.pbx.code ?? 'unknown'}`}
            to="/admin/system"
          />
          <Kpi
            label="Tenants"
            value={tenants.data ? tenants.data.tenants.length : '…'}
            foot={people !== undefined ? `${plural(people, 'person', 'people')} across all tenants` : ' '}
            to="/admin/tenants"
          />
          <Kpi
            label="Changes allowed on"
            small
            value={!s ? '…' : s.writes.readOnly ? 'Nothing (read-only)' : s.writes.tenants.includes('*') ? 'Every tenant' : 'Every customer’s tenant'}
            foot={s && !s.writes.readOnly ? writesFoot(s) : 'Every tenant is read-only'}
            to="/admin/system"
          />
          <Kpi
            label="Needs attention"
            value={changes.data ? attention.length : '…'}
            foot={
              attention.length ? (
                <Chip tone="crit" small>
                  Staff must check {attention.length === 1 ? 'it' : 'them'}
                </Chip>
              ) : (
                'No failed rollbacks'
              )
            }
            to="/admin/activity?status=needs_attention"
          />
        </section>

        {s && s.notices.length > 0 && (
          <Notice tone="warn" title="Running with restrictions">
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {s.notices.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </Notice>
        )}

        <Card title="What’s keeping features off" action={<Link to="/admin/updates">Updates</Link>}>
          {blockers.error ? (
            <LoadError error={blockers.error} retry={blockers.reload} />
          ) : !blockers.data ? (
            <Loading label="Looking for what keeps features off" />
          ) : blockers.data.blockers.length === 0 ? (
            <Empty title="Nothing">Every feature can work for every customer.</Empty>
          ) : (
            <ul className="plain-list blocker-list">
              {blockers.data.blockers.map((b) => (
                <li key={b.id} className="blocker-row">
                  <span className="cell-stack">
                    <span>
                      <Link to={b.to}>
                        <strong>{b.title}</strong>
                      </Link>
                    </span>
                    <small className="muted">{b.why}</small>
                  </span>
                  <Chip tone={b.tone} small>
                    {b.customers === blockers.data!.customers ? 'Every customer' : plural(b.customers, 'customer')}
                  </Chip>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="cols">
          <div className="col-main">
            <Card title="Recent changes" action={<Link to="/admin/activity">All activity</Link>}>
              {changes.error ? (
                <LoadError error={changes.error} retry={changes.reload} />
              ) : !changes.data ? (
                <Loading />
              ) : all.length === 0 ? (
                <Empty title="No changes yet">Changes made in any tenant's portal (or by Leland) show up here.</Empty>
              ) : (
                <ChangeSetFeed items={all.slice(0, 10)} onOpen={setOpen} showTenant />
              )}
            </Card>
          </div>
          <div className="col-side">
            <Card title="Needs attention" action={attention.length > 0 ? <Link to="/admin/activity?status=needs_attention">View all</Link> : undefined}>
              {attention.length === 0 ? (
                <Empty title="All clear">No change sets are waiting on staff.</Empty>
              ) : (
                <ChangeSetFeed items={attention.slice(0, 5)} onOpen={setOpen} showTenant />
              )}
            </Card>
            <Card title="Waiting for approval" action={waiting.length > 0 ? <Link to="/admin/activity?status=planned">View all</Link> : undefined}>
              {waiting.length === 0 ? <Empty title="None waiting" /> : <ChangeSetFeed items={waiting.slice(0, 5)} onOpen={setOpen} showTenant />}
            </Card>
            {s && (
              <Card title="Before going live" action={<Link to="/admin/system">System</Link>}>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {unverified > 0 ? (
                    <span>
                      <strong>{unverified}</strong> of {s.endpoints.length} PBX endpoints still need checking on the 69 build.
                    </span>
                  ) : (
                    <span>All {s.endpoints.length} PBX endpoints the portal uses are verified on the 69 build.</span>
                  )}
                  {s.store === 'memory' && <span className="muted">Sign-ins and change history are kept in memory, so a restart clears them.</span>}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Drawer open={!!open} onClose={() => setOpen(null)} title={open?.title ?? ''} subtitle={open?.tenant}>
        {open && (
          <ChangeSetDetail
            cs={open}
            extra={
              <Link to={`/t/${open.tenant}/history`} className="btn secondary small" style={{ alignSelf: 'flex-start' }}>
                Open in the tenant's history
              </Link>
            }
          />
        )}
      </Drawer>
    </>
  );
}

/** Where changes are allowed (every feature can change there). */
function writesFoot(s: SystemInfo): string {
  if (s.writes.tenants.includes('*')) return 'Every feature, every company';
  const linked = s.writes.linked ?? 0;
  const tests = s.writes.tenants.length ? ` · test tenants: ${s.writes.tenants.join(', ')}` : '';
  return `${plural(linked, 'customer')}${tests}`;
}
